'use client';

import React, { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@zealy/design-system';
import { TelegramBrand } from '@zealy/icons';
import { useTelegramLogin } from '@zealy/queries';
import { sleep } from '@zealy/utils';

import { envConfig } from '#app/config';
import { toast } from '#components/Toaster';

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

interface TelegramLoginWidgetProps {
  onSuccess?: () => void;
  buttonText?: string;
  buttonProps?: {
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    className?: string;
    variant?: "filled" | "muted" | "ghost" | "dashed";
    color?: "default" | "cta" | "destructive";
  };
}

export const TelegramLoginWidget: React.FC<TelegramLoginWidgetProps> = ({ onSuccess, buttonText, buttonProps }) => {
  const t = useTranslations('settings.linked-accounts.telegram');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isAlreadyLinkedModalOpen, setIsAlreadyLinkedModalOpen] = useState(false);
  const [linkedEmail, setLinkedEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const telegramLogin = useTelegramLogin();

  // Ref to track pending Telegram ID and prevent duplicate requests
  const pendingTelegramIdRef = useRef<number | null>(null);

  const handleTelegramAuth = async (user: TelegramUser) => {
    // Dedupe: if already processing this telegram ID, skip
    if (pendingTelegramIdRef.current === user.id) {
      return;
    }

    pendingTelegramIdRef.current = user.id;
    setIsProcessing(true);

    try {
      // Wait for DB replication to catch up before making the API call
      // This helps prevent race conditions when user disconnects and immediately reconnects
      await sleep(500);

      await telegramLogin.mutateAsync(user);
      toast.success(t('connected'));
      onSuccess?.();
    } catch (error) {
      console.error('Failed to authenticate with Telegram:', error);

      // Check if this is a "telegram already linked" error
      if (error instanceof Error && 'type' in error && error.type === 'TELEGRAM_ALREADY_LINKED') {
        const typedError = error as Error & {
          type: 'TELEGRAM_ALREADY_LINKED';
          linkedEmail: string | null;
        };
        setLinkedEmail(typedError.linkedEmail);
        setIsAlreadyLinkedModalOpen(true);
      } else {
        setIsErrorModalOpen(true);
      }
    } finally {
      pendingTelegramIdRef.current = null;
      setIsProcessing(false);
    }
  };

  const initiateOAuth = () => {
    setIsLoading(true);

    const botId = envConfig.telegram.botId;
    if (!botId || !/^\d+$/.test(botId)) {
      console.error(
        'Telegram botId must be the numeric bot ID (digits only). Check NEXT_PUBLIC_TELEGRAM_BOT_ID.',
      );
      setIsLoading(false);
      setIsErrorModalOpen(true);
      return;
    }

    const origin = window.location.origin;
    const authUrl =
      `https://oauth.telegram.org/auth?bot_id=${encodeURIComponent(botId)}` +
      `&origin=${encodeURIComponent(origin)}&request_access=write`;

    try {
      const popup = window.open(
        authUrl,
        'telegram_auth',
        'width=550,height=470,scrollbars=yes,resizable=yes',
      );

      if (!popup) {
        throw new Error('Popup blocked');
      }

      const OAUTH_ORIGINS = ['https://oauth.telegram.org', 'https://oauth.tg.dev'];
      let done = false;
      let pollTimer: number = -1;

      const onMessage = (event: MessageEvent) => {
        if (!OAUTH_ORIGINS.includes(event.origin)) return;
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (data?.event === 'auth_result' && data?.result) {
            done = true;
            window.removeEventListener('message', onMessage as EventListener);
            if (pollTimer !== -1) clearInterval(pollTimer);
            setIsLoading(false);
            popup.close();
            handleTelegramAuth(data.result as TelegramUser);
          }
        } catch {
          // ignore
        }
      };

      window.addEventListener('message', onMessage as EventListener);

      // Poll for user closing the popup without completing auth
      pollTimer = window.setInterval(() => {
        if (popup.closed) {
          if (pollTimer !== -1) clearInterval(pollTimer);
          window.removeEventListener('message', onMessage as EventListener);
          setIsLoading(false);
          if (!done) setIsErrorModalOpen(true);
        }
      }, 500);
    } catch (error) {
      console.error('Failed to open Telegram auth popup:', error);
      setIsLoading(false);
      setIsErrorModalOpen(true);
    }
  };

  const handleRetry = () => {
    setIsErrorModalOpen(false);
    initiateOAuth();
  };

  return (
    <>
      <Button
        variant={buttonProps?.variant || "muted"}
        color={buttonProps?.color || "cta"}
        size={buttonProps?.size}
        onClick={initiateOAuth}
        loading={isLoading || isProcessing}
        className={buttonProps?.className || "flex items-center gap-150"}
        leftIcon={<TelegramBrand />}
      >
        {buttonText || t('connect')}
      </Button>

      <Dialog open={isErrorModalOpen} onOpenChange={setIsErrorModalOpen}>
        <DialogContent className="p-300 flex flex-col gap-250 w-[450px] max-w-[95vw]">
          <DialogHeader className="flex flex-col gap-100">
            <DialogTitle className="text-primary label-lg">
              {t('connection-issue.title')}
            </DialogTitle>
            <p className="body-component-md">{t('connection-issue.description')}</p>
          </DialogHeader>

          <div className="flex flex-col gap-200">
            <Button
              variant="muted"
              color="cta"
              href="/docs/contributor/account#telegram"
              target="_blank"
              as={Link}
              className="w-full"
            >
              {t('connection-issue.get-help')}
            </Button>

            <Button variant="ghost" color="default" onClick={handleRetry} className="w-full">
              {t('connection-issue.try-again')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isAlreadyLinkedModalOpen} onOpenChange={setIsAlreadyLinkedModalOpen}>
        <DialogContent className="p-300 flex flex-col gap-250 w-[450px] max-w-[95vw]">
          <DialogHeader className="flex flex-col gap-100">
            <DialogTitle className="text-primary label-lg">{t('already-linked.title')}</DialogTitle>
            <p className="body-component-md">
              {linkedEmail
                ? t.rich('already-linked.description', {
                    email: linkedEmail,
                    strong: chunks => <strong>{chunks}</strong>,
                  })
                : t.rich('already-linked.description-no-email', {
                    strong: chunks => <strong>{chunks}</strong>,
                  })}
            </p>
          </DialogHeader>

          <div className="flex flex-col gap-200">
            <Button
              variant="muted"
              color="cta"
              href="/docs/contributor/account#telegram"
              target="_blank"
              as={Link}
              className="w-full"
            >
              {t('already-linked.contact-support')}
            </Button>

            <Button variant="ghost" color="default" onClick={handleRetry} className="w-full">
              {t('connection-issue.try-again')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
